export const colors = {
  primaryBackgroundColor: "#0D1117",
  borderColor: "#8A8A8A",

  primaryDarkBlue: "#3D4E5B", //Grey Blue
  secondaryDarkBlue: "#406EA8", //Navy blue
  cedePrimaryColor: "#5EA6FF", //Blue
  cedePrimaryColorDark: "#75B3FF", // Another blue color
  cedePrimaryColorLight: "#9acfed", // This color is not part of the DS can change at any time
  cedeSecondaryColor: "#A6FB74", //Green
  cedeSuccessColor: "#A6FB74", // Same color as above but this is not a primary color it is a success color
  cedeWarningColor: "#FFA726",
  cedeWarningLightColor: "#E2C395",
  cedeErrorColor: "#f44335",
  cedeSecondaryColorDark: "#0B7400", // This color is not part of the DS can change at any time
  cedePrimaryBackgroundColor: "#0A0B0b", //Black
  cedeTextPrimaryColor: "#FFFFFF", //White
  cedeTextSecondaryColor: "rgba(255, 255, 255, 0.7)",
  cedeSecondaryBackgroundColor: "#1B1B1B", //Black
  cedeTextGrey: "rgba(255, 255, 255, 0.5)",
  cedeTextDisabled: "rgba(255, 255, 255, 0.5)",
  cedeSkeleton: "rgba(255, 255, 255, 0.2)",
  cedeComponentHr: "rgba(255, 255, 255, 0.12)",
  cedeComponentBackgroundColor: "rgba(25, 90, 174, 0.08)",
  cedeComponentBorderGreyColor: "#A3A3A3",
  cedeComponentHoveredBackgroundColor: "rgba(255, 255, 255, 0.18)",
  cedePaperBackgroundColor: "#121b26",
  cedePaperLightBackgroundColor: "#373737",
  cedePaperDarkBackgroundColor: "rgba(255, 255, 255, 0.08)",
  cedePaperGradientBackground:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.13) 100%), #121212",
  cedePaperModalGradientBackground:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), #121212",
  cedeBubbleBackgroundColor: "#616161E5",
  cedeGradientPrimaryBackground: "linear-gradient(140.84deg, #5EA6FF -14.8%, #A6FB75 127.29%);",
  cedeBuyColor: "#11cb81",
  cedeSellColor: "#f5465d",
  cedeHelpBackgroundColor: "#404040",
  cedeMobileBackground: "#0D1117",
};

export const lightColors: typeof colors = {
  ...colors,
  cedePrimaryBackgroundColor: "#FFFEFD",
  cedeTextPrimaryColor: "#1B2026",
  cedeTextSecondaryColor: "#525860",
  cedeHelpBackgroundColor: "#EDEDED",
  cedePaperLightBackgroundColor: "#EDEDED",
  cedePaperDarkBackgroundColor: "#F6F6F6",
  cedePaperBackgroundColor: "#FFFEFD",
  cedeTextDisabled: "rgba(54, 63, 77, 0.5)",
  cedeSkeleton: "rgba(54, 63, 77, 0.2)",
  cedeComponentBackgroundColor: "#F6F6F6",
  cedeSuccessColor: "rgb(91, 188, 33)",
  cedeComponentHr: "#EDEDED",
};

type CustomColors = typeof colors;

const palette = (colors: CustomColors, mode: "dark" | "light" = "dark") => ({
  mode,
  background: {
    default: colors.primaryBackgroundColor,
    paper: colors.cedePaperBackgroundColor,
    lightPaper: colors.cedePaperLightBackgroundColor,
    darkPaper: colors.cedePaperDarkBackgroundColor,
    component: colors.cedeComponentBackgroundColor,
    help: colors.cedeHelpBackgroundColor,
  },
  primary: { main: colors.cedePrimaryColor, dark: colors.cedePrimaryColorDark },
  secondary: { main: colors.cedeSecondaryColor, dark: colors.secondaryDarkBlue },
  text: {
    primary: colors.cedeTextPrimaryColor,
    secondary: colors.cedeTextSecondaryColor,
    disabled: colors.cedeTextDisabled,
  },
  action: {
    disabledBackground: colors.cedeComponentHr,
    disabled: colors.cedeTextDisabled,
  },
  success: {
    main: colors.cedeSuccessColor,
  },
  warning: {
    main: colors.cedeWarningColor,
    light: colors.cedeWarningLightColor,
  },
  error: {
    main: colors.cedeErrorColor,
  },
  logoBorderColor: colors.cedePrimaryColor,
  ...colors,
});

const custom = {
  zIndex: {
    baseLayer: 1,
    mediumLayer: 5,
    topLayer: 10,
    extraLayer: 9999,
  },
  storeSize: {
    width: "370px",
    tabWidth: "450px",
    bigTabWidth: "1265px",
    requestTabWidth: "600px",
    height: "600px",
    tabHeight: "600px",
    bigTabHeight: "800px",
    requestTabHeight: "500px",
    widgetWidth: "100%",
    tradeWidgetHeight: "600px",
    widgetHeight: "565px",
    widgetMinWidth: "250px",
    widgetMaxWidth: "500px",
  },
  navBarSize: {
    height: "94px",
    widgetHeight: "35px",
  },
  defaultBoxRadius: "6px",
  largeBoxRadius: "8px",
  mediumBoxRadius: "8px",
  buttonSmallRadius: "6px",
  buttonDefaultRadius: "8px",
  buttonRoundedRadius: "400px",
  smallGutterMargin: "8px",
  gutterMargin: "16px",
  mediumGutterMargin: "28px",
  bigGutterMargin: "40px",
  cedeLinkBigContentMargin: "60px",
  cedeLinkSmallContentMargin: "16px",
  cedeLinkBigContentMaxWidth: "1315px",
  inputRadius: {
    default: 4,
    rounded: 5,
  },
  imgSize: {
    small: "32px",
    medium: "56px",
    large: "110px",
  },
  iconSize: {
    small: "16px",
    medium: "20px",
    large: "29px",
  },
  linkLogoWidth: {
    small: "99px",
    medium: "151px",
    large: "230px",
  },
  linkLogoHeight: {
    small: "15px",
    medium: "23px",
    large: "35px",
  },
  inputWidth: {
    small: "256px",
  },
  circleSize: {
    small: "8px",
  },
  opacity: {
    alert: 0.6,
    legend: 0.7,
  },
  textSize: {
    small: "9px",
    medium: "11px",
    large: "13px",
  },
  iconUnderlayMargin: 4,
  portfolioItemWidth: "170px",
  portfolioItemHeight: "60px",
  linkInactiveFilterWidth: "160px",
  linkActiveFilterWidth: "200px",
  txHistoryItem: {
    height: 50,
  },
  txHistoryList: {
    dateLineHeight: 74,
    dateLineMargin: 16,
    txDate: {
      margin: 15,
      height: 35,
    },
    txPlace: {
      margin: 16,
      height: 50,
    },
    txItem: {
      collapsed: 75,
      toggled: 145,
    },
  },
  dappListItemHeight: 55,
  gradientButtonBackground: colors.cedeGradientPrimaryBackground,
  connectAccount: {
    background: "hsla(214, 35%, 11%, 1)",
    backgroundRGB: "#121b26",
    hoverBackground: "hsla(214, 35%, 20%, 1)",
    borderColor: "rgba(25, 90, 174, 0.2)",
    borderRadius: "16px",
    buttonBorderColor: "rgba(25, 90, 174, 0.5)",
  },
  drawerBackdropColor: "rgba(0, 0, 0, 0.5)",
  drawerBackground: "#121b26",
  darkBackground: "rgba(29, 29, 29, 0.5)",
  selectBackground: "rgba(33, 33, 33, 0.8)",
  selectorTrigger: {
    border: "#0F2D4E",
    borderActive: "#1F4170",
    background: "rgba(25, 90, 174, 0.08)",
    hoverBackground: "rgba(25, 90, 174, 0.12)",
    iconBackground: "rgba(25, 90, 174, 0.12)",
    chevronColor: "#5080DA",
  },
  tableRowHoverColor: "rgba(255, 255, 255, 0.08)",
  scrollbarThumb: "rgba(255, 255, 255, 0.7)",
  scrollbarTrack: "rgba(60, 60, 60, 0.5)",
  contrast: {
    shadePrimary08: "rgba(25, 90, 174, 0.08)",
    shadePrimary16: "rgba(25, 90, 174, 0.16)",
    shadePrimary30: "rgba(25, 90, 174, 0.3)",
    shade10: "rgba(0, 0, 0, 0.1)",
    shade20: "rgba(0, 0, 0, 0.2)",
    shade70: "rgba(0, 0, 0, 0.7)",
    shade87: "rgba(0, 0, 0, 0.87)",
  },
  notice: {
    background: "rgba(240, 185, 11, 0.13)",
    color: "rgba(240, 185, 11, 1)",
  },
  containerBorder: "1px solid rgba(25, 90, 174, 0.16)",
  componentBlur: "blur(12px)",
  textGradient: {
    primary: "linear-gradient(180deg, #FFFFFF 0%, #999999 100%)",
  },
  borderRadius: {
    small: "8px",
    medium: "12px",
    large: "16px",
  },
  medalColors: {
    gold: "#FFD54F",
    silver: "#BDBDBD",
    bronze: "#F57C00",
    default: "#5EA6FF",
  },
  medalRowBackgrounds: {
    gold: "linear-gradient(90deg, rgba(255, 213, 79, 0.25) 0%, rgba(26, 28, 34, 0) 100%)",
    silver: "linear-gradient(90deg, rgba(189, 189, 189, 0.25) 0%, rgba(26, 28, 34, 0) 100%)",
    bronze: "linear-gradient(90deg, rgba(245, 124, 0, 0.25) 0%, rgba(26, 28, 34, 0) 100%)",
    default: "transparent",
  },
  campaignStatus: {
    future: {
      borderColor: "rgba(25, 90, 174, 0.5)",
      color: "#5EA6FF",
    },
    live: {
      borderColor: "rgba(166, 251, 117, 0.5)",
      color: "#A6FB75",
    },
    past: {
      borderColor: "rgba(255, 255, 255, 0.42)",
      color: "rgba(255, 255, 255, 0.42)",
    },
    paused: {
      borderColor: "rgba(255, 167, 38, 0.5)",
      color: "#FFA726",
    },
  },
  animation: {
    cardHover: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  status: {
    background: "#29B6F6",
  },
  mobileBackground: colors.cedeMobileBackground,
  informationCard: {
    backgroundColor: "rgba(25, 90, 174, 0.1)",
    border: `1px solid color-mix(in srgb, ${colors.cedePrimaryColor} 30%, transparent)`,
    titleColor: "#8dbfe3",
    textColor: "#95bae1e0",
  },
  usdtColor: "#28ad50",
};

export const InterFont = "'Inter', sans-serif";

export const typography = () => ({
  fontFamily: InterFont,
  h3: {
    fontSize: "48px",
    lineHeight: "120%",
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  h4: {
    fontSize: "34px",
    lineHeight: "120%",
    fontWeight: 600,
    letterSpacing: "-4%",
  },
  h5: {
    fontSize: "24px",
    lineHeight: "120%",
    fontWeight: 600,
    letterSpacing: "-0.005em",
  },
  h6: {
    fontSize: "20px",
    lineHeight: "160%",
    fontWeight: 600,
    letterSpacing: "-0.005em",
  },
  subtitle1: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  subtitle2: {
    fontSize: "14px",
    lineHeight: "140%",
    fontWeight: "bold",
  },
  body1: {
    fontSize: "16px",
    lineHeight: "140%",
    fontWeight: 400,
  },
  body2: {
    fontSize: "14px",
    lineHeight: "134%",
    fontWeight: 400,
  },
  caption: {
    fontSize: "11px",
    lineHeight: "140%",
    fontWeight: 400,
  },
  overline: {
    fontSize: "12px",
    lineHeight: "135%",
    fontWeight: 400,
    letterSpacing: "0.001em",
  },
  // Components Configs
  badgeLabel: {
    fontSize: "12px",
    lineHeight: "20px",
    fontWeight: 500,
  },
  buttonLarge: {
    fontSize: "16px",
    lineHeight: "26px",
    fontWeight: "bold",
  },
  buttonMedium: {
    fontSize: "14px",
    lineHeight: "24px",
    fontWeight: "bold",
  },
  buttonSmall: {
    fontSize: "13px",
    lineHeight: "22px",
    fontWeight: 600,
    letterSpacing: "0px",
  },
  chip: {
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: 400,
    letterSpacing: "0.1px",
  },
  helperText: {
    fontSize: "12px",
    lineHeight: "20px",
    fontWeight: 400,
    letterSpacing: "0.1px",
  },
  inputText: {
    fontSize: "14px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  inputLabel: {
    fontSize: "12px",
    lineHeight: "12px",
    fontWeight: 400,
    letterSpacing: "0.1px",
  },
  menuItem: {
    fontSize: "16px",
    lineHeight: "150%",
    fontWeight: 600,
  },
  tooltip: {
    fontSize: "10px",
    lineHeight: "14px",
    fontWeight: 500,
  },
  campaignStatus: {
    fontSize: "32px",
    lineHeight: "46px",
    fontWeight: 700,
  },
  notice: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    letterSpacing: "0.1px",
  },
  linkTitle: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "28.8px",
    letterSpacing: "0%",
  },
  linkSubtitle: {
    fontSize: "16px",
    fontWeight: 300,
    lineHeight: "25.6px",
    letterSpacing: "0%",
  },
});

export const breakpoints = {
  values: {
    xs: 0,
    sm: 360, // 0-767px (mobile)
    md: 768, // 768-1279px (tablet)
    lg: 1280, // 1280px and above (desktop)
    xl: 1280,
  },
};

export const shape = {
  borderRadius: 4,
};

export const themeConfig = { palette, custom, typography, breakpoints, shape };
